import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs, BaseQueryApi } from "@reduxjs/toolkit/query/react";
import type { RootState } from "@state/store";

import { fetchAndSignMondayData } from "@state/mondaySlice";
import { isTokenExpired } from "@utils/jwt";
import * as crypto from "@utils/crypto";

export const baseQuery: BaseQueryFn = async (args: string | FetchArgs, api: BaseQueryApi, extraOptions: object) => {
  const { getState, dispatch } = api;
  const state = getState() as RootState;
  let token = state.monday.token;
  const shouldEncrypt = import.meta.env.ENVIRONMENT === 'prod';

  const rawArgs: FetchArgs = typeof args === "string" ? { url: args } : { ...args };

  // Encrypt payload
  if (shouldEncrypt && rawArgs.body && typeof rawArgs.body === "object") {
    const encrypted = await crypto.encrypt(rawArgs.body);
    rawArgs.body = JSON.stringify({ data: encrypted });
    rawArgs.headers = {
      ...rawArgs.headers,
      "Content-Type": "application/json",
    };
  }

  // Get and set initial token
  if (!token) {
    await dispatch(fetchAndSignMondayData());
    token = (getState() as RootState).monday.token;
  }

  // Check if token exists and verify expiry - generate new token if expired
  if (token && (await isTokenExpired(token))) {
    await dispatch(fetchAndSignMondayData());
    token = (getState() as RootState).monday.token;
  }

  const baseQuery = fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_API_URL}/api`,
    prepareHeaders: (headers) => {
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
    responseHandler: async (response) => {
      const resText = await response.text();
      if (!shouldEncrypt || !resText.length) return resText ? JSON.parse(resText) : null;

      try {
        const parsed = JSON.parse(resText);
        if ("data" in parsed) {
          return await crypto.decrypt(parsed.data);
        }
        return parsed;
      } catch (error) {
        console.error("Failed to decrypt response:", error);
        return resText;
      }
    },
  });

  return baseQuery(rawArgs, api, extraOptions);
};
